// ═══════════════════════════════════════════════════════════════
// VERSION CHECK — poller + reload coordinator (module singleton)
//
// Fetches /version.json and compares it to the build's APP_VERSION.
// When a newer version has been deployed it flips `updateAvailable`
// once and notifies subscribers (the VersionWatcher UI). It also
// coordinates a SAFE reload: VersionWatcher arms a pending reload,
// and the nav handlers consume it at a screen-change boundary so we
// never interrupt a user mid-form. A second trigger covers kiosks:
// after IDLE_APPLY_MS with no input, the update applies on its own
// (see "Idle auto-apply" below).
//
// Every reload path here preserves localStorage `sb-*` (the Supabase
// session) → a reload never logs the user out.
// ═══════════════════════════════════════════════════════════════

import { APP_VERSION, VERSION_MANIFEST_URL, type VersionManifest } from './version';

type Listener = () => void;

const listeners = new Set<Listener>();
let updateAvailable = false;
let updateNotes: string[] = [];
/** The deployed version our notes describe — so a SECOND deploy re-reads them. */
let notedVersion = '';
let pendingReload = false;
let lastCheck = 0;
let started = false;

const MIN_INTERVAL_MS = 60_000; // don't refetch more than once a minute
const POLL_INTERVAL_MS = 5 * 60_000;

// ── Idle auto-apply ──
// A kiosk tablet parked on one Plant screen never navigates, so the
// nav-boundary reload never fires and the tablet runs the old build
// forever. When an update is available AND there has been no user input
// for IDLE_APPLY_MS, reload on our own. "Idle" alone doesn't prove the
// reload is safe, so four guards stack on top of the input window:
//   • an open dialog defers it — commit-at-the-end dialogs (loss
//     interview, reply compose) hold uncommitted drafts, and the doc
//     viewer is a dialog too, so a propped-up tablet showing a shop
//     drawing keeps its drawing
//   • holdIdleReload() defers it — a 500MB plan upload runs for many
//     minutes without producing a single input event
//   • a pre-flight manifest fetch defers it while offline — reloading
//     with no network commits the browser error page, and on a kiosk
//     nobody is there to retry
//   • a one-shot sessionStorage guard stops the reload LOOP if a
//     misbehaving cache keeps serving the old index.html
// The last-location snapshot is already in storage (AppWithJobSync
// writes it on every navigation and live tab report), so the fresh
// build reopens the same screen — nothing extra to persist here.
const IDLE_APPLY_MS = 5 * 60_000;
const IDLE_CHECK_INTERVAL_MS = 30_000;
/** Monotonic clock: an NTP step can't fake five idle minutes. */
let lastInputAt = performance.now();
let idleHolds = 0;
let idlePreflightRunning = false;
/** A version an idle reload already aimed for and MISSED — never retry it. */
let idleReloadFailedFor: string | null = null;

const IDLE_RELOAD_TARGET_KEY = 'mos:idle-reload-target';

/**
 * Defer the idle auto-reload until the returned release fn is called — for
 * work a reload would destroy that emits no input events while it runs
 * (a multi-minute plan upload). Nesting holds is fine; releasing twice is a
 * no-op. The nav-boundary and "Update now" paths are NOT deferred: those
 * fire from the user's own click.
 */
export function holdIdleReload(): () => void {
  idleHolds++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    idleHolds--;
  };
}

function idleFor(): number {
  return performance.now() - lastInputAt;
}

/** Radix dialogs/sheets, alert dialogs, and the doc viewer all carry these. */
function dialogOpen(): boolean {
  return !!document.querySelector('[role="dialog"], [role="alertdialog"]');
}

async function idleApplyCheck(): Promise<void> {
  if (!updateAvailable || idlePreflightRunning) return;
  if (idleFor() < IDLE_APPLY_MS) return;
  if (idleHolds > 0 || dialogOpen()) return;
  if (notedVersion !== '' && notedVersion === idleReloadFailedFor) return;
  idlePreflightRunning = true;
  try {
    // Pre-flight: prove the server is reachable and STILL newer before
    // burning the running page.
    const res = await fetch(VERSION_MANIFEST_URL, { cache: 'no-store' });
    if (!res.ok) return;
    const data = (await res.json()) as VersionManifest;
    if (!data?.version || data.version === APP_VERSION) return;
    if (data.version === idleReloadFailedFor) return;
    // The fetch took time — re-check every guard so input (or a dialog)
    // that appeared mid-flight still wins.
    if (idleFor() < IDLE_APPLY_MS || idleHolds > 0 || dialogOpen()) return;
    try { sessionStorage.setItem(IDLE_RELOAD_TARGET_KEY, data.version); } catch { /* ignore */ }
    reloadNow();
  } catch {
    /* offline blip — the next tick retries */
  } finally {
    idlePreflightRunning = false;
  }
}

function emit() {
  listeners.forEach((l) => {
    try { l(); } catch { /* ignore listener errors */ }
  });
}

/** Subscribe to "update available" changes. Returns an unsubscribe fn. */
export function subscribe(cb: Listener): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export function getUpdateAvailable(): boolean {
  return updateAvailable;
}

/**
 * What shipped since the build this tab is running: `count` is how many
 * changes, `lines` is the display list — repeats folded into one line with
 * a tally, so five HR fixes read as one row, not five.
 */
export function getUpdateSummary(): { count: number; lines: string[] } {
  const seen = new Map<string, number>();
  updateNotes.forEach((n) => seen.set(n, (seen.get(n) ?? 0) + 1));
  return {
    count: updateNotes.length,
    lines: [...seen].map(([note, n]) => (n > 1 ? `${note} (${n})` : note)),
  };
}

/**
 * Walk the manifest's commit log back to the build we're running and collect
 * the notes above it — that's "what changed since you loaded MOS", however
 * many deploys ago that was. If our build isn't in the window (very old tab,
 * or a local build) we don't guess a count: show the deploy's own headline.
 */
function notesSinceRunningBuild(data: VersionManifest): string[] {
  const changes = Array.isArray(data.changes) ? data.changes : [];
  const mine = changes.findIndex((c) => c?.v === APP_VERSION);
  if (mine >= 0) {
    return changes.slice(0, mine).map((c) => (typeof c?.n === 'string' ? c.n.trim() : '')).filter(Boolean);
  }
  const note = typeof data.note === 'string' ? data.note.trim() : '';
  return note ? [note] : [];
}

/**
 * Fetch the manifest and flip the flag if a different version is live.
 * Keeps polling AFTER the first hit: a tab left open across three deploys
 * should list everything since its own build, not just the first deploy it
 * happened to notice. Only a manifest we haven't noted yet re-emits.
 */
export async function checkNow(force = false): Promise<void> {
  const now = Date.now();
  if (!force && now - lastCheck < MIN_INTERVAL_MS) return;
  lastCheck = now;

  try {
    const res = await fetch(VERSION_MANIFEST_URL, { cache: 'no-store' });
    if (!res.ok) return; // dev 404 / transient — ignore
    const data = (await res.json()) as VersionManifest;
    if (data?.version && data.version !== APP_VERSION && data.version !== notedVersion) {
      notedVersion = data.version;
      updateAvailable = true;
      updateNotes = notesSinceRunningBuild(data);
      emit();
    }
  } catch {
    /* offline or network blip — ignore, we'll retry on the next trigger */
  }
}

/** Begin watching: initial check, refocus check, and a periodic poll. */
export function startVersionWatch(): void {
  if (started) return; // StrictMode / double-mount guard
  started = true;

  void checkNow(true);

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void checkNow();
    });
  }

  setInterval(() => { void checkNow(); }, POLL_INTERVAL_MS);

  if (typeof window !== 'undefined') {
    // If the LAST idle reload didn't move us to the version it aimed for
    // (a misbehaving cache re-served the old index.html), never aim at
    // that version again — otherwise an unattended kiosk reloads every
    // ~5 minutes forever. The card and the nav-boundary reload still
    // work, and a NEWER deploy re-arms the idle path. After a SUCCESSFUL
    // idle reload main.tsx's clean-first-run reset has already cleared
    // this key; it only survives when the reload missed.
    try {
      const target = sessionStorage.getItem(IDLE_RELOAD_TARGET_KEY);
      if (target) {
        sessionStorage.removeItem(IDLE_RELOAD_TARGET_KEY);
        if (target !== APP_VERSION) idleReloadFailedFor = target;
      }
    } catch { /* storage disabled — ignore */ }

    // Capture phase so a component calling stopPropagation can't hide the
    // user from us; passive so we never add scroll/tap latency.
    // `pointermove` counts a watching mouse as presence (an untouched
    // tablet emits none), `wheel` counts a mouse-wheel reader, `input`
    // catches virtual keyboards whose keydown reporting is unreliable
    // (Android IME).
    const markInput = () => { lastInputAt = performance.now(); };
    (['pointerdown', 'pointermove', 'keydown', 'touchstart', 'wheel', 'input'] as const).forEach((ev) => {
      window.addEventListener(ev, markInput, { capture: true, passive: true });
    });

    setInterval(() => { void idleApplyCheck(); }, IDLE_CHECK_INTERVAL_MS);
  }
}

/** Arm a reload to happen at the next safe navigation boundary. */
export function requestReloadOnNextNav(): void {
  pendingReload = true;
}

/**
 * If a reload is armed, reload now and report true. Called from nav handlers.
 *
 * The click that trips this is DISCARDED (we reload instead of navigating), so
 * the caller passes `beforeReload` to record where that click was heading —
 * otherwise the fresh tab restores the screen the user was leaving and the
 * update looks like it threw them backwards.
 */
export function consumeReloadIfPending(beforeReload?: () => void): boolean {
  if (pendingReload) {
    try { beforeReload?.(); } catch { /* never block the reload on this */ }
    window.location.reload();
    return true;
  }
  return false;
}

/** Immediate reload (the banner's "Update now" button). */
export function reloadNow(): void {
  window.location.reload();
}
